import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PatientSearchInput from '@/components/PatientSearchInput';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import ResponsiveNavigation from '@/components/ResponsiveNavigation';
import { 
  ArrowLeft,
  Loader2,
  AlertCircle,
  ShoppingCart
} from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { formularyApi, type FormularyItem } from '@/services/formularyService';
import { selectCurrentClinicId, selectCurrentClinic, selectClinicsLoading } from '@/store/slices/authSlice';

const CreateOrder = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const itemId = searchParams.get('itemId');
  const pharmacyName = searchParams.get('pharmacy');
  
  console.log('[CreateOrder] URL params:', { itemId, pharmacyName });

  // Helper function to format badge text (remove underscores, capitalize) hell yeah
  const formatBadgeText = (text: string): string => {
    if (!text) return '';
    return text
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to parse medication string into names and dosages (same as Catalogue)
  const parseMedicationString = (medicationString: string) => {
    if (!medicationString) {
      return { names: 'N/A', dosages: '' };
    }
    
    let names = '';
    let dosages = '';
    
    try {
      // For the specific pattern: "10.0mg / 0.5mg / 2.5mg / 1.0ml 2.5mg 10ml Lidocaine/Cyanocobalamin/Semaglutide"
      // Look for medication names at the end (capital letters with slashes)
      const nameMatch = medicationString.match(/([A-Z][a-zA-Z/]+)$/);
      
      if (nameMatch) {
        // Extract medication names and replace slashes with dots
        names = nameMatch[1].replace(/\//g, ' • ');
        
        // Extract everything before the medication names as dosage info
        const beforeNames = medicationString.replace(nameMatch[1], '').trim();
        // Replace slashes with dots in dosages too
        dosages = beforeNames.replace(/\s*\/\s*/g, ' • ');
      } else {
        // Fallback: split by spaces and try to identify medication names vs dosages
        const parts = medicationString.split(/\s+/);
        const medicationNames: string[] = [];
        const dosageParts: string[] = [];
        
        for (const part of parts) {
          // Check if this looks like a medication name (starts with capital, no numbers, not a unit)
          if (/^[A-Z][a-z]+/.test(part) && !/\d/.test(part) && !['mg', 'ml', 'mL', 'mcg', 'units'].includes(part)) {
            medicationNames.push(part);
          }
          // Check if this looks like a dosage (contains numbers and units)
          else if (/\d+(\.\d+)?\s*(mg|ml|mL|mcg|units)/i.test(part)) {
            dosageParts.push(part);
          }
        }
        
        if (medicationNames.length > 0) {
          names = medicationNames.join(' • ');
          dosages = dosageParts.join(' • ');
        } else {
          // Last fallback: replace all slashes with dots
          names = medicationString.replace(/\//g, ' • ');
          dosages = '';
        }
      }
    } catch (error) {
      console.warn('Error parsing medication string:', error);
      names = medicationString || 'N/A';
      dosages = '';
    }
    
    return { 
      names: names || 'N/A', 
      dosages: dosages || '' 
    };
  };

  // Helper function to get formatted medication names
  const getMedicationNames = (item: FormularyItem): string => {
    const { names } = parseMedicationString(item.name || '');
    return names;
  };

  // Helper function to get formatted dosage information
  const getMedicationDosages = (item: FormularyItem): string => {
    const { dosages } = parseMedicationString(item.name || '');
    return dosages;
  };
  
  // Redux selectors
  const currentClinicId = useSelector(selectCurrentClinicId);
  const currentClinic = useSelector(selectCurrentClinic);
  const clinicsLoading = useSelector(selectClinicsLoading);
  
  // Form state - Always use batch order logic
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [selectedFormulary, setSelectedFormulary] = useState('');
  const [selectedPharmacy, setSelectedPharmacy] = useState('');
  const [selectedShippingMethod, setSelectedShippingMethod] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [directions, setDirections] = useState('');
  const [shippingDestination, setShippingDestination] = useState('clinic');
  
  // Formulary item state
  const [formularyItem, setFormularyItem] = useState<FormularyItem | null>(null);
  const [isLoadingItem, setIsLoadingItem] = useState(false);
  const [itemError, setItemError] = useState<string | null>(null);

  // Fetch formulary item when itemId is available
  useEffect(() => {
    const fetchFormularyItem = async () => {
      console.log('[CreateOrder] useEffect triggered with:', { itemId, currentClinicId, clinicsLoading });
      
      // GATE: Wait for clinic loading to complete
      if (clinicsLoading) {
        console.log('[CreateOrder] Waiting for clinics to load...');
        return;
      }
      
      if (!itemId || !currentClinicId) {
        console.log('[CreateOrder] Missing required params, skipping fetch');
        return;
      }
      
      console.log('[CreateOrder] Fetching formulary item:', itemId);
      setIsLoadingItem(true);
      setItemError(null);
      
      try {
        // Construct HATEOAS href for the individual item (relative to base path)
        const itemHref = `clinic/${currentClinicId}/formulary/${itemId}`;
        console.log('[CreateOrder] Making API call with href:', itemHref);
        const item = await formularyApi.getFormularyItem(itemHref);
        
        console.log('[CreateOrder] Fetched formulary item:', item);
        setFormularyItem(item);
        
        // Pre-select pharmacy if specified in URL
        if (pharmacyName && item.pharmacies) {
          const matchingPharmacy = item.pharmacies.find(
            pharmacy => pharmacy.name.toLowerCase() === pharmacyName.toLowerCase()
          );
          if (matchingPharmacy) {
            setSelectedPharmacy(matchingPharmacy.id);
            console.log('[CreateOrder] Pre-selected pharmacy:', matchingPharmacy.name);
          } else {
            console.log('[CreateOrder] Pharmacy not found:', pharmacyName, 'Available:', item.pharmacies.map(p => p.name));
          }
        }
        
      } catch (error) {
        console.error('[CreateOrder] Failed to fetch formulary item:', error);
        setItemError('Failed to load formulary item details. You can still create an order manually.');
      } finally {
        setIsLoadingItem(false);
      }
    };

    fetchFormularyItem();
  }, [itemId, currentClinicId, clinicsLoading, pharmacyName]);

  // Handle pharmacy selection and reset shipping method when pharmacy changes
  const handlePharmacyChange = (pharmacyId: string) => {
    setSelectedPharmacy(pharmacyId);
    setSelectedShippingMethod(''); // Reset shipping when pharmacy changes
  };

  // Handle shipping method selection
  const handleShippingMethodChange = (shippingMethod: string) => {
    setSelectedShippingMethod(shippingMethod);
  };

  // Get available pharmacies from formulary item
  const getAvailablePharmacies = () => {
    if (!formularyItem?.pharmacies) return [];
    return formularyItem.pharmacies.filter(pharmacy => pharmacy.available !== false);
  };

  // Get shipping options for selected pharmacy
  const getShippingOptions = () => {
    if (!formularyItem?.pharmacies || !selectedPharmacy) return [];
    const selectedPharmacyObj = formularyItem.pharmacies.find(p => p.id === selectedPharmacy);
    return selectedPharmacyObj?.shipping || [];
  };

  const formularies = [
    { id: '1', name: 'Standard Formulary' },
    { id: '2', name: 'Premium Formulary' },
    { id: '3', name: 'Custom Formulary' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentClinicId) {
      console.error('No clinic ID available');
      return;
    }

    // Validate required fields
    if (selectedPatients.length === 0) {
      alert('Please select at least one patient.');
      return;
    }

    if (formularyItem && !selectedPharmacy) {
      alert('Please select a pharmacy.');
      return;
    }

    if (selectedPharmacy && !selectedShippingMethod) {
      alert('Please select a shipping method.');
      return;
    }

    // Always use batch logic - patients array
    const patientIds = selectedPatients.length > 0 ? selectedPatients : [];

    // Build the order payload
    const orderPayload = {
      patient_id: patientIds,
      formulary_id: formularyItem?.id || selectedFormulary,
      quantity: parseInt(quantity),
      directions: directions.trim() || undefined,
      pharmacy_id: selectedPharmacy || undefined,
      ship_to_patient: shippingDestination === 'patient',
      refill: false
    };

    // Remove undefined values
    const cleanPayload = Object.fromEntries(
      Object.entries(orderPayload).filter(([, value]) => value !== undefined)
    );

    console.log('Submitting order:', cleanPayload);

    try {
      // Use HATEOAS orders href from current clinic
      if (!currentClinic?.orders?.href) {
        throw new Error('No orders endpoint available');
      }
      
      const { apiCall } = await import('../services/apiService');
      const result = await apiCall(currentClinic.orders.href, {
        method: 'POST',
        body: JSON.stringify(cleanPayload)
      });

      console.log('Order created successfully:', result);
      
      // Show success message and navigate to orders page
      alert('Order created successfully!');
      navigate('/orders');
      
    } catch (error) {
      console.error('Error creating order:', error);
      alert(`Failed to create order: ${error}`);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 font-lato">
      <ResponsiveNavigation />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button and Title */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="mb-6 text-gray-600 hover:text-vitl-navy font-lato transition-colors p-0 h-auto font-normal"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>

        {/* Order Form */}
        <Card className="w-full max-w-3xl lg:max-w-4xl border-l-4 border-l-emerald-500 shadow-lg shadow-emerald-100/50">
          <CardContent className="p-6">
            {/* Header */}
            <div className="mb-2">
              <div className="text-xl font-syne font-medium text-vitl-navy flex items-center space-x-3 border-b border-emerald-100 pb-2">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-500 rounded-full flex items-center justify-center text-white font-syne font-medium text-lg">
                  <ShoppingCart className="w-6 h-6" />
                </div>
                <div>
                  <div>Create New Order</div>
                  <p className="text-sm text-vitl-dark/70 font-lato font-normal">
                    Create a new order for patients with formulary items
                  </p>
                </div>
              </div>
            </div>
            {/* Selected Formulary Item Display */}
            {(isLoadingItem || clinicsLoading) && (
              <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600 mr-3" />
                  <p className="text-blue-700 text-sm font-medium">
                    {clinicsLoading ? 'Loading clinic data...' : 'Loading formulary item details...'}
                  </p>
                </div>
              </div>
            )}

            {itemError && (
              <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-red-600 mr-3" />
                  <p className="text-red-700 text-sm font-medium">{itemError}</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6 py-4">
              {/* Order Details */}
              <div className="space-y-4">
                {formularyItem && !isLoadingItem && !clinicsLoading && (
                  <Card className="border-l-4 border-l-emerald-500 shadow-sm hover:shadow-md transition-all duration-200 shadow-emerald-100/50">
                    <CardContent className="p-3 sm:p-4 lg:p-6">
                      {/* Tablet Layout (Same as Catalogue) */}
                      <div className="space-y-3">
                        {/* Header Row */}
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-lato font-medium text-vitl-navy text-sm sm:text-base lg:text-lg mb-1 leading-snug">
                              {getMedicationNames(formularyItem)}
                            </h3>
                            {getMedicationDosages(formularyItem) && (
                              <p className="font-lato text-vitl-dark/60 text-sm mb-1 leading-snug">
                                {getMedicationDosages(formularyItem)}
                              </p>
                            )}
                            <div className="flex items-center gap-3 flex-wrap">
                              <div>
                                <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-emerald-200 leading-none mb-1">Category</p>
                                <span className="block px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-lato rounded border border-emerald-200">
                                  {formatBadgeText(formularyItem.category)}
                                </span>
                              </div>
                              <div>
                                <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-indigo-200 leading-none mb-1">Route</p>
                                <span className="block px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-lato rounded border border-indigo-200">
                                  {formatBadgeText(formularyItem.route)}
                                </span>
                              </div>
                              <div>
                                <p className="inline-block text-[10px] text-gray-900 uppercase tracking-wider border-b border-amber-200 leading-none mb-1">Form</p>
                                <span className="block px-2 py-0.5 bg-amber-50 text-amber-700 text-xs font-lato rounded border border-amber-200">
                                  {formatBadgeText(formularyItem.form)}
                                </span>
                              </div>
                            </div>
                          </div>
                
                        </div>

                        {/* Description */}
                        {formularyItem.description && (
                          <p className="text-vitl-dark/70 font-lato text-sm leading-relaxed line-clamp-2">
                            {formularyItem.description}
                          </p>
                        )}

                        {/* Details Grid */}
                        <div className="grid grid-cols-2 gap-3 pt-1">
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato mb-0.5">Selected Pharmacy</p>
                            <p className="text-vitl-navy font-lato text-sm font-medium">
                              {selectedPharmacy ? 
                                getAvailablePharmacies().find((p: any) => p.id === selectedPharmacy)?.name || 'Not selected' 
                                : 'Not selected'
                              }
                            </p>
                          </div>
                          <div>
                            <p className="text-vitl-dark/50 text-xs font-lato mb-0.5">Price</p>
                            <p className="text-vitl-navy font-lato text-sm font-medium">
                              {selectedPharmacy ? 
                                `$${(getAvailablePharmacies().find((p: any) => p.id === selectedPharmacy)?.price || 0).toFixed(2)}`
                                : 'N/A'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {!formularyItem && (
                  <div>
                    <Label htmlFor="formulary" className="text-sm font-medium text-gray-700">
                      Formulary *
                    </Label>
                    <Select value={selectedFormulary} onValueChange={setSelectedFormulary}>
                      <SelectTrigger className="mt-1 h-12 bg-gray-50 border-gray-200 font-lato focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-emerald-400 focus:outline-none">
                        <SelectValue placeholder="Select a formulary" />
                      </SelectTrigger>
                      <SelectContent className="font-lato border-l-4 border-l-emerald-500">
                        {formularies.map((formulary) => (
                          <SelectItem key={formulary.id} value={formulary.id}>
                            {formulary.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label htmlFor="quantity" className="text-sm font-medium text-gray-700">
                    Quantity *
                  </Label>
                  <Input
                    id="quantity"
                    type="number"
                    min="1"
                    step="1"
                    value={quantity}
                    onChange={(e: any) => {
                      const value = parseInt(e.target.value);
                      if (value >= 1 || e.target.value === '') {
                        setQuantity(e.target.value);
                      }
                    }}
                    placeholder="Enter quantity"
                    className="mt-1 h-11 bg-white border-gray-300 font-lato transition-colors focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:ring-opacity-20"
                  />
                </div>

                {/* Pharmacy Selection */}
                {formularyItem && (
                  <div>
                    <Label htmlFor="pharmacy" className="text-sm font-medium text-gray-700">
                      Pharmacy *
                    </Label>
                    <Select value={selectedPharmacy} onValueChange={handlePharmacyChange}>
                      <SelectTrigger className="mt-1 h-11 bg-white border-gray-300 font-lato transition-colors focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:ring-opacity-20">
                        <SelectValue placeholder="Select a pharmacy" />
                      </SelectTrigger>
                      <SelectContent className="font-lato">
                        {getAvailablePharmacies().map((pharmacy: any) => (
                          <SelectItem key={pharmacy.id} value={pharmacy.id}>
                            <div className="flex justify-between items-center w-full">
                              <span>{pharmacy.name}</span>
                              <span className="text-sm text-gray-500 ml-4">${pharmacy.price.toFixed(2)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {getAvailablePharmacies().length === 0 && (
                      <p className="mt-1 text-sm text-red-600 font-lato">No available pharmacies for this formulary item.</p>
                    )}
                  </div>
                )}

                {/* Directions */}
                <div>
                  <Label htmlFor="directions" className="text-sm font-medium text-gray-700">
                    Directions for Use <span className="text-gray-500">(Optional)</span>
                  </Label>
                  <Textarea
                    id="directions"
                    value={directions}
                    onChange={(e: any) => setDirections(e.target.value)}
                    placeholder="Enter directions for use"
                    className="mt-1 min-h-24 bg-white border-gray-300 font-lato transition-colors focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 focus:ring-opacity-20 resize-none"
                  />
                </div>
              </div>

              {/* Patient Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">Patient Information</h3>
                
                <div>
                  <PatientSearchInput
                    selectedPatients={selectedPatients}
                    onPatientsSelect={setSelectedPatients}
                    placeholder="Search and select patients..."
                    label="Patients"
                    required={true}
                    allowMultiple={true}
                  />
                </div>
              </div>

              {/* Shipping Information */}
              {selectedPharmacy && getShippingOptions().length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-syne font-medium text-vitl-navy border-b border-emerald-100 pb-2">Shipping Information</h3>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      Shipping Method *
                    </Label>
                    <div className="mt-2 space-y-3 p-3 border-l-4 border-l-emerald-500 border border-gray-300 rounded-md shadow-sm">
                      {getShippingOptions().map((shippingOption: string) => (
                        <div key={shippingOption} className="flex items-start space-x-3">
                          <div className="flex items-center h-5">
                            <Checkbox
                              id={`shipping-${shippingOption}`}
                              checked={selectedShippingMethod === shippingOption}
                              onCheckedChange={() => handleShippingMethodChange(shippingOption)}
                              className="data-[state=checked]:bg-vitl-green data-[state=checked]:border-vitl-green"
                            />
                          </div>
                          <div className="flex-1">
                            <label htmlFor={`shipping-${shippingOption}`} className="text-sm font-lato font-medium text-gray-700 cursor-pointer capitalize">
                              {shippingOption}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      Shipping Destination *
                    </Label>
                    <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="space-y-3">
                        {[
                          { value: 'clinic', label: 'Clinic', description: 'Ship to clinic address for pickup' },
                          { value: 'patient', label: 'Patient', description: 'Ship directly to patient address' }
                        ].map((option) => (
                          <div key={option.value} className="flex items-start space-x-3">
                            <div className="flex items-center h-5">
                              <Checkbox
                                id={`shipping-${option.value}`}
                                checked={shippingDestination === option.value}
                                onCheckedChange={() => setShippingDestination(option.value)}
                                className="data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                              />
                            </div>
                            <div className="flex-1">
                              <label htmlFor={`shipping-${option.value}`} className="text-sm font-medium text-gray-900 cursor-pointer">
                                {option.label}
                              </label>
                              <p className="text-xs text-gray-600">{option.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-3 pt-6 border-t border-emerald-100">
                <Button 
                  type="submit"
                  className="bg-gradient-to-r from-emerald-600 to-teal-500 hover:from-emerald-700 hover:to-teal-600 text-white"
                >
                  Create Order
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CreateOrder;
